import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiSlice } from '../api/apiSlice';
import { RootState } from '../types';

// Define types
export interface Department {
  _id: string;
  id?: string;
  name: string;
  manager: string;
  managerUserId?: string | null;
  isSalesTeam?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DepartmentSummary {
  departmentId: string;
  employeeCount: number;
  employees?: Array<{
    id: string;
    name: string;
    pto: { total: number; used: number; remaining: number };
    sickTime: { total: number; used: number; remaining: number };
    wfh: { total: number; used: number; remaining: number };
  }>;
  timeOffByWeek: Record<string, Array<{
    employee: string;
    type: string;
    startDate: string;
    endDate: string;
    status: string;
  }>>;
  coverageAnalysis: {
    departmentCoverage: number;
    conflicts: Array<{
      employeeName: string;
      startDate: string;
      endDate: string;
      type: string;
    }>;
    recommendations: string[];
  };
}

// For backward compatibility with existing component
export interface FormattedDepartmentSummary {
  departmentTotals: {
    name: string;
    employeeCount: number;
    pto: {
      total: number;
      used: number;
    };
    sickTime: {
      total: number;
      used: number;
    };
    wfh: {
      total: number;
      used: number;
    };
  };
  monthlyUsage: Array<{
    pto: number;
    sick: number;
    wfh: number;
  }>;
  employeeSummaries: Array<{
    id: string;
    name: string;
    pto: {
      total: number;
      used: number;
      remaining: number;
    };
    sickTime: {
      total: number;
      used: number;
      remaining: number;
    };
    wfh: {
      total: number;
      used: number;
      remaining: number;
    };
  }> | never[];
}

export interface DepartmentState {
  selectedDepartment: Department | null;
  filter: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

// Initial state
const initialState: DepartmentState = {
  selectedDepartment: null,
  filter: '',
  sortBy: 'name',
  sortOrder: 'asc',
};

// Create the slice
const departmentSlice = createSlice({
  name: 'departments',
  initialState,
  reducers: {
    setSelectedDepartment: (state, action: PayloadAction<Department | null>) => {
      state.selectedDepartment = action.payload;
    },
    setFilter: (state, action: PayloadAction<string>) => {
      state.filter = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
  },
});

// Export actions and reducer
export const { setSelectedDepartment, setFilter, setSortBy, setSortOrder } = departmentSlice.actions;
export default departmentSlice.reducer;

// Selectors
export const selectSelectedDepartment = (state: RootState) => state.departments.selectedDepartment;
export const selectDepartmentFilter = (state: RootState) => state.departments.filter;
export const selectDepartmentSortBy = (state: RootState) => state.departments.sortBy;
export const selectDepartmentSortOrder = (state: RootState) => state.departments.sortOrder;

// API endpoints
export const departmentApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDepartments: builder.query<Department[], void>({
      query: () => '/departments',
      transformResponse: (response: { success: boolean; data: Department[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Department' as const, id: _id })),
              { type: 'Department', id: 'LIST' },
            ]
          : [{ type: 'Department', id: 'LIST' }],
    }),
    getDepartmentById: builder.query<Department, string>({
      query: (id) => `/departments/${id}`,
      transformResponse: (response: { success: boolean; data: Department }) => response.data,
      providesTags: (result, error, id) => [{ type: 'Department', id }],
    }),
    getDepartmentSummary: builder.query<FormattedDepartmentSummary, string>({
      query: (id) => `/time-off/department/${id}/summary`,
      transformResponse: (response: { success: boolean; data: DepartmentSummary }) => {
        const rawData = response.data;
        
        // Initialize the formatted summary with default values
        const formattedSummary: FormattedDepartmentSummary = {
          departmentTotals: {
            name: '',
            employeeCount: rawData.employeeCount || 0,
            pto: {
              total: 0,
              used: 0,
            },
            sickTime: {
              total: 0,
              used: 0,
            },
            wfh: {
              total: 0,
              used: 0,
            },
          },
          monthlyUsage: [],
          employeeSummaries: [],
        };
        
        if (rawData.employees && rawData.employees.length > 0) {
          formattedSummary.employeeSummaries = rawData.employees.map((employee) => ({
            id: employee.id,
            name: employee.name,
            pto: employee.pto,
            sickTime: employee.sickTime,
            wfh: employee.wfh,
          }));

          // Aggregate totals from employee data
          formattedSummary.employeeSummaries.forEach((emp) => {
            formattedSummary.departmentTotals.pto.total += emp.pto?.total ?? 0;
            formattedSummary.departmentTotals.pto.used += emp.pto?.used ?? 0;
            formattedSummary.departmentTotals.sickTime.total += emp.sickTime?.total ?? 0;
            formattedSummary.departmentTotals.sickTime.used += emp.sickTime?.used ?? 0;
            formattedSummary.departmentTotals.wfh.total += emp.wfh?.total ?? 0;
            formattedSummary.departmentTotals.wfh.used += emp.wfh?.used ?? 0;
          });
        } else {
          // Backward-compatible fallback using timeOffByWeek
          const employeeMap = new Map<string, string[]>();
          Object.values(rawData.timeOffByWeek || {}).forEach(entries => {
            entries.forEach(entry => {
              if (!employeeMap.has(entry.employee)) {
                employeeMap.set(entry.employee, []);
              }
              employeeMap.get(entry.employee)?.push(entry.type);
            });
          });

          formattedSummary.employeeSummaries = Array.from(employeeMap.entries()).map(([name, types]) => {
            const ptoCount = types.filter(t => t === 'PTO').length;
            const sickCount = types.filter(t => t === 'SICK').length;
            const wfhCount = types.filter(t => t === 'WFH').length;

            return {
              id: name.replace(/\s+/g, '-').toLowerCase(),
              name,
              pto: {
                total: 10,
                used: ptoCount,
                remaining: 10 - ptoCount,
              },
              sickTime: {
                total: 5,
                used: sickCount,
                remaining: 5 - sickCount,
              },
              wfh: {
                total: 3,
                used: wfhCount,
                remaining: 3 - wfhCount,
              },
            };
          });
        }
        
        console.log('Transformed department summary:', formattedSummary);
        
        return formattedSummary;
      },
      providesTags: (result, error, id) => [
        { type: 'Department', id },
        { type: 'TimeOff', id: 'LIST' },
      ],
    }),
    addDepartment: builder.mutation<Department, Partial<Department>>({
      query: (department) => ({
        url: '/departments',
        method: 'POST',
        body: department,
      }),
      transformResponse: (response: { success: boolean; data: Department }) => response.data,
      invalidatesTags: [{ type: 'Department', id: 'LIST' }],
    }),
    updateDepartment: builder.mutation<Department, { id: string; department: Partial<Department> }>({
      query: ({ id, department }) => ({
        url: `/departments/${id}`,
        method: 'PUT',
        body: department,
      }),
      transformResponse: (response: { success: boolean; data: Department }) => response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Department', id },
        { type: 'Department', id: 'LIST' },
      ],
    }),
    deleteDepartment: builder.mutation<void, string>({
      query: (id) => ({
        url: `/departments/${id}`,
        method: 'DELETE',
      }),
      // The row is hard-deleted and employees.department_id is ON DELETE SET
      // NULL, so anyone still assigned loses their department: refetch them too.
      // RTK Query invalidates on a rejected mutation as well, so a 409 (people
      // were assigned since the page loaded) refreshes the counts the same way.
      invalidatesTags: [
        { type: 'Department', id: 'LIST' },
        { type: 'Employee', id: 'LIST' },
      ],
    }),
  }),
});

// Export hooks
export const {
  useGetDepartmentsQuery,
  useGetDepartmentByIdQuery,
  useGetDepartmentSummaryQuery,
  useAddDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
} = departmentApiSlice;
